import { CollateralSpecificMatch, CollateralTypeDto, CollateralTypeEnum } from '../models';

export function isCashCollateral(csMatch: CollateralSpecificMatch): boolean {
  return isCashCollateralType(csMatch.type);
}

export function isNonCashCollateral(csMatch: CollateralSpecificMatch): boolean {
  return isNonCashCollateralType(csMatch.type);
}

export function isCashNonCashCollateral(csMatch: CollateralSpecificMatch): boolean {
  return isCashNonCashCollateralType(csMatch.type);
}

export function isCashCollateralType(collateralType: CollateralTypeDto): boolean {
  return collateralType === CollateralTypeEnum.CASH;
}

export function isNonCashCollateralType(collateralType: CollateralTypeDto): boolean {
  return collateralType === CollateralTypeEnum.NONCASH;
}

export function isCashNonCashCollateralType(collateralType: CollateralTypeDto): boolean {
  return collateralType === CollateralTypeEnum.CASH_AND_NONCASH;
}

/**
 * Splits a match's availability into its cash and non-cash buckets. A `CASH_AND_NONCASH` split is
 * lendable either way, so it lands in BOTH — it is one pool the borrower may take under either
 * collateral, not two.
 *
 * Generic over the split so the borrower-api and locates-api DTOs both fit: they are structurally
 * identical, and the alternative is a cast at one of the two call sites.
 *
 * Lives here rather than in `locate-match.utils` — which re-exports it — because that module pulls
 * in `@angular/forms` and `@pebula/utils`, and specs that mock `@sg/common` cannot then reach it.
 */
export function groupAvailabilitySplitByCollateralType<T extends { collateralType?: CollateralTypeDto }>(
  matchAvailabilitySplit: T[]
): Record<'cash' | 'nonCash', T[]> {
  const collateralTypeGroups: Record<'cash' | 'nonCash', T[]> = { cash: [], nonCash: [] };

  matchAvailabilitySplit.forEach((split) => {
    switch (split.collateralType) {
      case CollateralTypeEnum.CASH:
        collateralTypeGroups.cash.push(split);
        break;
      case CollateralTypeEnum.NONCASH:
        collateralTypeGroups.nonCash.push(split);
        break;
      case CollateralTypeEnum.CASH_AND_NONCASH:
        collateralTypeGroups.cash.push(split);
        collateralTypeGroups.nonCash.push(split);
        break;
    }
  });

  return collateralTypeGroups;
}
